// src/components/legal/AccessibilityModal.tsx
// Accessibility Statement — content can be overridden from Firestore (siteContent/accessibilityStatement).

import { useEffect, useState } from 'react';
import { doc, getDoc }         from 'firebase/firestore';
import { db }                  from '../../lib/firebase';
import { useLegalModalStore }  from '../../stores/useLegalModalStore';
import { LegalModal }          from './LegalModal';

// ── Hardcoded fallback content ────────────────────────────────────────────────
const FALLBACK_HTML = `
<h2>Our Commitment</h2>
<p>
  START is committed to ensuring that our platform is accessible to everyone, including
  people with disabilities. We continually improve the user experience for all, and apply
  relevant accessibility standards as part of our development process.
</p>

<hr />

<h2>Conformance Status</h2>
<p>
  We aim to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1,
  Level AA</strong>, as published by the World Wide Web Consortium (W3C). We evaluate
  our platform on an ongoing basis and address issues as they are identified.
</p>
<p>
  Current conformance status: <strong>Partially conformant</strong> — some areas are
  still being improved (see "Known Limitations" below).
</p>

<hr />

<h2>Measures We Take</h2>
<p>Our accessibility efforts include:</p>
<ul>
  <li>All interactive elements are keyboard-navigable (Tab, Shift+Tab, Enter, Escape).</li>
  <li>Focus is managed correctly in modals and dialogs — focus trapping is in place and focus returns to the trigger on close.</li>
  <li>All images and icons include descriptive <code>alt</code> text or <code>aria-label</code> attributes, or are marked <code>aria-hidden</code> when decorative.</li>
  <li>Colour contrast ratios meet or exceed the WCAG 2.1 AA minimum (4.5:1 for normal text, 3:1 for large text).</li>
  <li>Both dark and light themes are available, reducing strain for users with light sensitivity.</li>
  <li>Font sizes are specified in relative units (<code>rem</code>) and the UI remains usable when browser text size is increased up to 200%.</li>
  <li>The platform is fully operable with screen readers (tested with NVDA on Windows and VoiceOver on macOS/iOS).</li>
  <li>Forms include associated <code>&lt;label&gt;</code> elements and descriptive error messages.</li>
  <li>Animations respect the <code>prefers-reduced-motion</code> media query — all motion is disabled for users who request it.</li>
  <li>Audio content in exercises includes full transcripts available on request.</li>
</ul>

<hr />

<h2>Known Limitations</h2>
<p>
  Despite our efforts, the following areas may not yet fully meet WCAG 2.1 AA:
</p>
<ul>
  <li>
    <strong>Audio exercises</strong> — captions for some video-based listening exercises
    are in progress (expected: Q2 2026).
  </li>
  <li>
    <strong>Complex data tables</strong> — some progress-summary tables on the dashboard
    lack full header associations; this is being addressed.
  </li>
  <li>
    <strong>Third-party widgets</strong> — Stripe's payment form is provided by a third
    party; any accessibility concerns should be directed to
    <a href="https://stripe.com/accessibility" target="_blank" rel="noopener noreferrer">Stripe's Accessibility page</a>.
  </li>
</ul>

<hr />

<h2>Technical Specification</h2>
<p>
  The START platform relies on the following technologies for accessibility:
</p>
<ul>
  <li>HTML5 semantic elements (<code>&lt;nav&gt;</code>, <code>&lt;main&gt;</code>, <code>&lt;footer&gt;</code>, <code>&lt;article&gt;</code>, etc.)</li>
  <li>WAI-ARIA roles, states, and properties where native semantics are insufficient.</li>
  <li>CSS for visual presentation; no CSS is used to convey information not otherwise available in the markup.</li>
  <li>React for dynamic content updates, with live-region announcements (<code>aria-live</code>) for status messages.</li>
</ul>

<hr />

<h2>Feedback and Contact</h2>
<p>
  We welcome your feedback on the accessibility of START. If you experience barriers
  or have suggestions for improvement, please contact us:
</p>
<ul>
  <li><strong>Email:</strong> <a href="mailto:accessibility@startlearning.ge">accessibility@startlearning.ge</a></li>
  <li><strong>Response time:</strong> We aim to respond to accessibility feedback within <strong>5 business days</strong>.</li>
</ul>
<p>
  If you are not satisfied with our response, you may contact the relevant national
  supervisory authority in your country.
</p>

<hr />

<h2>Formal Complaints</h2>
<p>
  If you wish to make a formal complaint about our accessibility provision,
  please email <a href="mailto:accessibility@startlearning.ge">accessibility@startlearning.ge</a>
  with the subject line <strong>"Accessibility Complaint"</strong>. We will acknowledge
  your complaint within 5 working days and aim to resolve it within 20 working days.
</p>

<hr />

<h2>Assessment Approach</h2>
<p>START assesses the accessibility of this platform by:</p>
<ul>
  <li>Self-evaluation during development using automated tools (Axe, Lighthouse).</li>
  <li>Manual keyboard-navigation testing on every major feature release.</li>
  <li>Screen reader testing with NVDA (Windows) and VoiceOver (macOS, iOS).</li>
  <li>Periodic third-party accessibility audits (planned annually).</li>
</ul>
<p>
  This statement was last reviewed on <strong>1 January 2026</strong>.
</p>
`;

// ── Component ─────────────────────────────────────────────────────────────────
export function AccessibilityModal() {
  const openModal = useLegalModalStore((s) => s.openModal);
  const isOpen    = openModal === 'accessibility';

  const [htmlContent, setHtmlContent] = useState<string>(FALLBACK_HTML);

  useEffect(() => {
    getDoc(doc(db, 'siteContent', 'accessibilityStatement'))
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          if (data?.html) setHtmlContent(data.html as string);
        }
      })
      .catch(() => {/* use fallback */});
  }, []);

  return (
    <LegalModal
      isOpen={isOpen}
      title="Accessibility Statement"
      lastUpdated="1 January 2026"
      htmlContent={htmlContent}
    />
  );
}